import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  MapPin, 
  Calendar, 
  Clock, 
  LogOut, 
  Menu, 
  RefreshCw,
  X,
  ChevronRight
} from 'lucide-react';

const navigationItems = [
  { name: 'Dispatch', href: '/dispatch', icon: LayoutDashboard },
  { name: 'Visits', href: '/visits', icon: Calendar },
  { name: 'Clients', href: '/clients', icon: Building2 },
  { name: 'Locations', href: '/locations', icon: MapPin },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Recurring', href: '/recurring-jobs', icon: RefreshCw },
  { name: 'Timesheets', href: '/timesheets', icon: Clock },
];

const pageTitles = {
  '/dispatch': 'Dispatch Board',
  '/visits': 'Visits',
  '/clients': 'Clients',
  '/locations': 'Locations',
  '/users': 'Users',
  '/recurring-jobs': 'Recurring Jobs',
  '/timesheets': 'Timesheets',
  '/cleaner': 'My Schedule',
};

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const pageTitle = pageTitles[location.pathname] || 'Dashboard';

  // For cleaners, show a simpler layout
  if (user?.role === 'CLEANER') {
    return (
      <div className="min-h-screen bg-slate-100">
        {/* Cleaner Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="px-4 sm:px-6 h-14 flex items-center justify-between">
            <h1 className="text-lg font-bold text-slate-900">CREWGRID</h1>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-slate-900">{user?.full_name}</div>
                <div className="text-xs text-slate-500">Cleaner</div>
              </div>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-slate-500 hover:text-slate-700"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="app-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 text-white
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-800">
          <Link to="/dispatch" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <LayoutDashboard className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold">CREWGRID</span>
          </Link>
          <button 
            className="lg:hidden p-1 text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-colors duration-150
                  ${isActive 
                    ? 'bg-orange-500 text-white' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }
                `}
                data-testid={`sidebar-nav-${item.name.toLowerCase()}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span>{item.name}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-sm font-medium">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.full_name}</div>
              <div className="text-xs text-slate-400">{user?.role}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8">
            {/* Left: Mobile menu + Page title */}
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-slate-700"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-semibold text-slate-900">{pageTitle}</h1>
            </div>

            {/* Right: User info + Logout */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <div className="text-sm font-medium text-slate-900">{user?.full_name}</div>
                <div className="text-xs text-slate-500">{user?.role}</div>
              </div>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-slate-500 hover:text-slate-700"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-[1400px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

// Keep the old Layout export for backwards compatibility during migration
export const Layout = AppLayout;
